import { useCallback, useEffect, useMemo, useState } from "react";
import { getAppointmentsByOrganizationAsync } from "../api/AppointmentApi";
import {
  Appointment,
  AppointmentStatus,
} from "../models/AppointmentModels";
import { PagedResult } from "../models/PagedModels";
import { Constants } from "../constants/constants";
import { monthRange } from "../utils/DateTime";

const DEFAULT_PAGE_SIZE = 20;

export interface AppointmentFilters {
  from?: string;
  to?: string;
  status?: AppointmentStatus;
  search?: string;
}

interface UseAppointmentsReturn {
  appointments: Appointment[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  loading: boolean;
  error: string | null;
  filters: AppointmentFilters;
  setFilters: (filters: AppointmentFilters) => void;
  setPage: (page: number) => void;
  reload: () => Promise<void>;
}

export const useAppointments = (
  organizationId: string | undefined,
): UseAppointmentsReturn => {
  const [page, setPage] = useState(1);
  const [filters, setFiltersState] = useState<AppointmentFilters>(() =>
    monthRange(),
  );
  const [data, setData] = useState<PagedResult<Appointment> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

  const load = useCallback(async () => {
    if (!organizationId || !token) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await getAppointmentsByOrganizationAsync(token, organizationId, {
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        from: filters.from,
        to: filters.to,
        search: filters.search?.trim() || undefined,
      });
      if (res.success && res.result) {
        setData(res.result);
        setError(null);
      } else {
        setError(res.message || "Failed to load appointments");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  }, [organizationId, token, page, filters.from, filters.to, filters.search]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilters = useCallback((next: AppointmentFilters) => {
    setPage(1);
    setFiltersState((prev) => ({ ...prev, ...next }));
  }, []);

  const reload = useCallback(async () => {
    setPage(1);
    await load();
  }, [load]);

  // The API filters by date range only; status is applied client-side.
  const appointments = useMemo(() => {
    const items = data?.items ?? [];
    if (filters.status === undefined) return items;
    return items.filter((appointment) => appointment.status === filters.status);
  }, [data, filters.status]);

  return {
    appointments,
    pagination: {
      page: data?.page ?? page,
      pageSize: data?.pageSize ?? DEFAULT_PAGE_SIZE,
      totalCount: data?.totalCount ?? 0,
      totalPages: data?.totalPages ?? 1,
      hasNext: data?.hasNext ?? false,
      hasPrevious: data?.hasPrevious ?? false,
    },
    loading,
    error,
    filters,
    setFilters,
    setPage,
    reload,
  };
};